export type EventLogMetricCategory =
  | 'schedule'
  | 'clinical'
  | 'billing'
  | 'patients'
  | 'databases'
  | 'clinic_settings'
  | 'user_settings'
  | 'resources';

export const METRIC_CATEGORY_LABELS: Record<EventLogMetricCategory, string> = {
  schedule: 'Schedule',
  clinical: 'Clinical',
  billing: 'Billing',
  patients: 'Patients',
  databases: 'Databases',
  clinic_settings: 'Clinic Settings',
  user_settings: 'User Settings',
  resources: 'Resources',
};

export const METRIC_CATEGORY_ORDER: EventLogMetricCategory[] = [
  'schedule',
  'clinical',
  'billing',
  'patients',
  'databases',
  'clinic_settings',
  'user_settings',
  'resources',
];

const METRIC_TO_CATEGORY: Record<string, EventLogMetricCategory> = {
  // Schedule
  appointments_created: 'schedule',
  appointments_checked_in: 'schedule',
  appointments_checked_out: 'schedule',

  // Clinical
  encounters_signed: 'clinical',
  voice_to_chart_uses: 'clinical',
  pcp_initial_reports: 'clinical',
  pcp_interim_reports: 'clinical',
  pcp_release_reports: 'clinical',
  ccs_sent: 'clinical',
  ccs_completed: 'clinical',

  // Billing
  purchases_created: 'billing',
  claims_sent: 'billing',
  claims_accepted: 'billing',
  payments_collected: 'billing',
  eras_uploaded: 'billing',
  npi_rejections: 'billing',
  enrollment_rejections: 'billing',
  format_837_rejections: 'billing',
  provider_loop_errors: 'billing',
  referring_provider_errors: 'billing',

  // Patients
  patients_created: 'patients',
  condition_reports_created: 'patients',
  condition_reports_sent: 'patients',
  condition_reports_opened: 'patients',
  exercise_plans_created: 'patients',
  google_review_links_sent: 'patients',
  healthcom_logins: 'patients',
  post_care_surveys_sent: 'patients',
  post_care_surveys_completed: 'patients',

  // Databases
  payors_added: 'databases',
  billing_codes_added: 'databases',
  no_show_cancel_fees_added: 'databases',
  no_show_cancel_fees_updated: 'databases',
  modalities_added: 'databases',
  supplements_added: 'databases',
  healthcare_providers_added: 'databases',
  tests_added: 'databases',

  // Clinic Settings
  clinic_logo_added: 'clinic_settings',
  facebook_linked: 'clinic_settings',
  google_review_url_added: 'clinic_settings',
  clinic_locations_added: 'clinic_settings',
  clinic_schedule_updates: 'clinic_settings',
  scheduling_preferences_updates: 'clinic_settings',
  survey_preferences_updates: 'clinic_settings',
  billing_global_settings_updates: 'clinic_settings',
  online_scheduling_url_updates: 'clinic_settings',
  online_bookings_user_toggles: 'clinic_settings',
  online_booking_treatment_updates: 'clinic_settings',
  kiosk_connections: 'clinic_settings',
  kiosk_disconnections: 'clinic_settings',
  plan_changes: 'clinic_settings',
  disciplines_added: 'clinic_settings',
  disciplines_updated: 'clinic_settings',
  packages_added: 'clinic_settings',
  packages_updated: 'clinic_settings',

  // User Settings
  users_added: 'user_settings',
  users_removed: 'user_settings',
  user_role_changes: 'user_settings',
  provider_billing_updates: 'user_settings',
  provider_schedule_updates: 'user_settings',
  provider_signatures_added: 'user_settings',
  provider_signature_deletes: 'user_settings',
  provider_profile_picture_updates: 'user_settings',
  billing_info_added: 'user_settings',
  commercial_numbers_added: 'user_settings',

  // Resources
  condition_references_accessed: 'resources',
  condition_references_edited: 'resources',
  clinical_evaluations_accessed: 'resources',
  treatment_techniques_accessed: 'resources',
  research_reviews_accessed: 'resources',
  marketing_campaigns_accessed: 'resources',
  knowledge_base_accessed: 'resources',
  forms_library_downloads: 'resources',
  features_suggested: 'resources',
  features_voted: 'resources',
};

export const EXCLUDED_METRICS = new Set(['active_users', 'active_time_minutes', 'session_count']);

export const categoryForMetric = (metricKey: string): EventLogMetricCategory => {
  return METRIC_TO_CATEGORY[metricKey] ?? 'resources';
};

const METRIC_ACRONYMS: Record<string, string> = {
  ccs: 'CCS',
  pcp: 'PCP',
  npi: 'NPI',
  url: 'URL',
  era: 'ERA',
  eras: 'ERAs',
  id: 'ID',
};

const METRIC_LABEL_OVERRIDES: Record<string, string> = {
  healthcom_logins: 'Daily HealthCom Usage',
};

export const labelForMetric = (metricKey: string): string => {
  if (METRIC_LABEL_OVERRIDES[metricKey]) {
    return METRIC_LABEL_OVERRIDES[metricKey];
  }

  return metricKey
    .split('_')
    .map(w => METRIC_ACRONYMS[w] ?? w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
};
